import { api } from '../../fetchWrapper';
import { logger } from '../../../utils/logger';
import { API_ENDPOINTS, getEndpoint, getEndpointWithId } from '../../apiConstants';
import {
  UserData,
  UserApiResponse,
  UserSearchParams,
  CreateUserPayload,
  UpdateUserPayload,
  UserRoleData,
  SaveUserRolePayload,
  AvailableRoleData,
  AvailableRoleApiResponse,
  PermissionData,
  RolePermissionPayload,
} from './users.types';

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const transformUser = (user: UserApiResponse): UserData => ({
  ...user,
  fullName: `${user.firstName} ${user.lastName}`.trim(),
});

const buildParams = (params: UserSearchParams) => ({
  searched: true,
  searchInactiveAlso: true, // Always include this parameter
  ...(params.firstResult !== undefined && { firstResult: params.firstResult }),
  ...(params.maxResult !== undefined && { maxResult: params.maxResult }),
  ...(params.clientId && { clientId: params.clientId }),
  ...(params.firstName && { firstName: params.firstName }),
  ...(params.lastName && { lastName: params.lastName }),
  ...(params.email && { email: params.email }),
});

const transformRole = (role: AvailableRoleApiResponse): AvailableRoleData => ({
  id: role.id,
  roleName: role.roleName,
});

// ============================================================================
// USER CRUD OPERATIONS
// ============================================================================

export const userApi = {
  // Fetch users with pagination and filtering
  fetch: async ({ clientCode, ...params }: { clientCode: string } & UserSearchParams): Promise<UserData[]> => {
    const endpoint = getEndpoint(clientCode, API_ENDPOINTS.USER.LIST);
    const response: UserApiResponse[] = await api.get(endpoint, buildParams(params));
    return response.map(transformUser);
  },

  // Get total count for pagination
  count: async ({ clientCode, ...params }: { clientCode: string } & Omit<UserSearchParams, 'firstResult' | 'maxResult'>): Promise<number> => {
    const endpoint = getEndpoint(clientCode, API_ENDPOINTS.USER.COUNT);
    const response = await api.get(endpoint, buildParams(params));
    
    // Handle both response formats: direct number or object with count property
    if (typeof response === 'number') {
      return response;
    } else if (response && typeof response === 'object' && 'count' in response) {
      return (response as { count: number }).count;
    } else {
      logger.warn('Unexpected count response format:', response);
      return 0;
    }
  },

  // Get single user by ID - use /projectag/admin/users/{userId} (path: /admin/users/{userId})
  getById: async ({ clientCode, userId }: { clientCode: string; userId: number }): Promise<UserData> => {
    const endpoint = getEndpoint(clientCode, API_ENDPOINTS.USER.LIST) + userId;
    const response: UserApiResponse = await api.get(endpoint);
    return transformUser(response);
  },

  // Create new user - POST users/, payload uses isActive
  create: async ({ clientCode, payload }: { clientCode: string; payload: CreateUserPayload & { isActive?: boolean } }): Promise<UserData> => {
    const endpoint = getEndpoint(clientCode, API_ENDPOINTS.USER.CREATE);
    const { active, ...rest } = payload as CreateUserPayload & { active?: boolean };
    const apiPayload = { ...rest, isActive: (payload as any).isActive ?? active ?? true };
    const response: any = await api.post(endpoint, apiPayload);
    if (response?.success === false) {
      const msg = response?.messages?.join(', ') || 'Failed to create user';
      throw new Error(msg);
    }
    const created = response?.object ?? response;
    return created ? transformUser(created) : (payload as unknown as UserData);
  },

  // Update existing user - PUT users/ with full user payload
  update: async ({ clientCode, payload }: { clientCode: string; payload: UpdateUserPayload }): Promise<UserData> => {
    const endpoint = getEndpoint(clientCode, API_ENDPOINTS.USER.UPDATE);
    const response: any = await api.put(endpoint, payload);
    if (response?.success === false) {
      const msg = response?.messages?.join(', ') || 'Failed to update user';
      throw new Error(msg);
    }
    const updated = response?.object ?? response;
    return updated ? transformUser(updated) : (payload as UserData);
  },
};

// ============================================================================
// ROLE MANAGEMENT
// ============================================================================

export const roleApi = {
  // Get user's assigned roles - GET userRole/?firstResult=-1&maxResult=0&userId={userId}
  getUserRoles: async ({ clientCode, userId, firstResult = -1, maxResult = 0 }: {
    clientCode: string; userId: number; firstResult?: number; maxResult?: number;
  }): Promise<AvailableRoleData[]> => {
    const endpoint = getEndpoint(clientCode, API_ENDPOINTS.USER.USER_ROLES);
    const response: any[] = await api.get(endpoint, { firstResult, maxResult, userId });
    return response.map((row: any) => {
      if (Array.isArray(row)) {
        const roleId = row[2] ?? row[0];
        const roleName = row[3] ?? row[1] ?? '';
        return { id: roleId, roleName };
      }
      return {
        id: row.roleId ?? row.id,
        roleName: row.roleName ?? '',
      };
    }).filter((r) => r.id != null && r.roleName);
  },

  // Get all available roles
  getAll: async ({ clientCode, firstResult = -1, maxResult = 0 }: {
    clientCode: string; firstResult?: number; maxResult?: number;
  }): Promise<AvailableRoleData[]> => {
    const endpoint = getEndpoint(clientCode, API_ENDPOINTS.USER.ROLES_LIST);
    const response: AvailableRoleApiResponse[] = await api.get(endpoint, { firstResult, maxResult });
    return response.map(transformRole);
  },

  // Get role by ID
  getById: async ({ clientCode, roleId }: { clientCode: string; roleId: number }): Promise<AvailableRoleData> => {
    const endpoint = getEndpointWithId(clientCode, API_ENDPOINTS.USER.ROLE_BY_ID, roleId);
    const response: AvailableRoleApiResponse = await api.get(endpoint);
    return transformRole(response);
  },

  // Create new role
  create: async ({ clientCode, payload }: { clientCode: string; payload: any }): Promise<void> => {
    const endpoint = getEndpoint(clientCode, API_ENDPOINTS.USER.CREATE_ROLE);
    return await api.post(endpoint, payload);
  },

  // Update role
  update: async ({ clientCode, payload }: { clientCode: string; payload: any }): Promise<void> => {
    const endpoint = getEndpoint(clientCode, API_ENDPOINTS.USER.UPDATE_ROLE);
    return await api.put(endpoint, payload);
  },

  // Save user role assignments
  saveUserRoles: async ({ clientCode, payload }: { clientCode: string; payload: SaveUserRolePayload }): Promise<void> => {
    const endpoint = getEndpoint(clientCode, API_ENDPOINTS.USER.SAVE_USER_ROLE);
    return await api.post(endpoint, payload);
  },
};

// ============================================================================
// PERMISSION MANAGEMENT
// ============================================================================

export const permissionApi = {
  // Get all permissions
  getAll: async ({ clientCode, firstResult = -1, maxResult = 0 }: {
    clientCode: string; firstResult?: number; maxResult?: number;
  }): Promise<PermissionData[]> => {
    const endpoint = getEndpoint(clientCode, API_ENDPOINTS.USER.PERMISSIONS);
    return await api.get(endpoint, { firstResult, maxResult });
  },

  // Get assigned permissions
  getAssigned: async ({ clientCode, firstResult = -1, maxResult = 0 }: {
    clientCode: string; firstResult?: number; maxResult?: number;
  }): Promise<PermissionData[]> => {
    const endpoint = getEndpoint(clientCode, API_ENDPOINTS.USER.ASSIGNED_PERMISSIONS);
    return await api.get(endpoint, { firstResult, maxResult });
  },

  // Save role permissions
  saveRolePermissions: async ({ clientCode, payload }: { clientCode: string; payload: RolePermissionPayload }): Promise<void> => {
    const endpoint = getEndpoint(clientCode, API_ENDPOINTS.USER.SAVE_ROLE_PERMISSIONS);
    return await api.post(endpoint, payload);
  },
};

// ============================================================================
// LEGACY EXPORTS (for backward compatibility)
// ============================================================================

export const fetchUsers = userApi.fetch;
export const fetchUsersCount = userApi.count;
export const fetchUserById = userApi.getById;
export const createUser = userApi.create;
export const updateUser = userApi.update;
export const fetchUserRoles = roleApi.getUserRoles;
export const fetchAvailableRoles = roleApi.getAll;
export const fetchRoles = roleApi.getAll;
export const fetchRoleById = roleApi.getById;
export const createRole = roleApi.create;
export const updateRole = roleApi.update;
export const saveUserRoles = roleApi.saveUserRoles;
export const fetchPermissions = permissionApi.getAll;
export const fetchAssignedPermissions = permissionApi.getAssigned;
export const saveRolePermissions = permissionApi.saveRolePermissions;
